import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { TableDataSourceItem } from "@/Common/Models/UI/Tables/ITableDataSourceItem";
import { ImmunizationStatus } from "../Constants/ImmunizationStatus";
import { ValueSetType } from "../Constants/ValueSetType";
import { CodeValue } from "../Models/CodeValue";
import { ImmunizationModel } from "../Models/ImmunizationModel";
import { QuebecVaccinationRegistryHelper } from "../QuebecVaccinationRegistryHelper";
import { BaseImmunizationTable, BaseImmunizationTableProps } from "./BaseImmunizationTable";

interface ImmunizationHistoryTableProps extends BaseImmunizationTableProps<ImmunizationModel> {
    onChangeStatusClick: (model: ImmunizationModel) => void;
    onDeleteClick: (model: ImmunizationModel) => void;
    onViewClick: (model: ImmunizationModel) => void;
    isCorrector: boolean;
    immunizationDeletePermissions: number[];
}

class ImmunizationHistoryTable extends BaseImmunizationTable<ImmunizationHistoryTableProps, ImmunizationModel> {
    protected title(): string {
        return cacheStore.GetString("QuebecVaccinationRegistry_VaccinationHistoryLabel");
    }

    private labels = {
        ImmunisationAgent: "QuebecVaccinationRegistry_AntigenImmunizingAgentLabel",
        AdministrationDate: "AdministeredDateDLabel",
        CommercialName: "QuebecVaccinationRegistry_CommercialNameLabel",
        QuantityAdministered: "QuebecVaccinationRegistry_QuantityAdministeredLabel",
        Status: "StatusLabel",
        Action: "ActionLabel",
        Override: "QuebecVaccinationRegistry_OverrideLabel",
        Reset: "QuebecVaccinationRegistry_ResetToForecastStatus"
    };

    private fieldNames = {
        ImmunisationAgent: "ImmunisationAgent",
        AdministrationDate: "AdministrationDate",
        CommercialName: "CommercialName",
        QuantityAdministered: "QuantityAdministered",
        Status: "Status",
        Action: "Action"
    };

    protected tableId(): string {
        return "vaccination-profile-history";
    }

    protected defaultTableOptions(): Immutable.List<MyleTableColumnOptions> {
        return Immutable.List([
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.ImmunisationAgent),
                FieldName: this.fieldNames.ImmunisationAgent,
                WidthPercentageDefault: 20
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.AdministrationDate),
                FieldName: this.fieldNames.AdministrationDate,
                WidthPercentageDefault: 16
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.CommercialName),
                FieldName: this.fieldNames.CommercialName,
                WidthPercentageDefault: 16
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.QuantityAdministered),
                FieldName: this.fieldNames.QuantityAdministered,
                WidthPercentageDefault: 16
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.Status),
                FieldName: this.fieldNames.Status,
                WidthPercentageDefault: 16
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.Action),
                FieldName: this.fieldNames.Action,
                WidthPercentageDefault: 16
            })
        ]);
    }

    protected getTableColumns(tableOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<TableDataSourceItem<ImmunizationModel>>(
            tableOptions,
            column => {
                switch (column.FieldName) {
                    case this.fieldNames.ImmunisationAgent:
                        return {
                            renderCell: item => <div>{QuebecVaccinationRegistryHelper.translateValueSetString(item.Model.Vaccine, ValueSetType.Agent)}</div>
                        };

                    case this.fieldNames.AdministrationDate:
                        return {
                            renderCell: item => <div>{this.formatDate(item.Model.Date)}</div>
                        };

                    case this.fieldNames.CommercialName:
                        return {
                            renderCell: item => <div>{item.Model.VaccineTradeName}</div>
                        };

                    case this.fieldNames.QuantityAdministered:
                        return {
                            renderCell: item => <div>{`${item.Model?.DoseSize ?? ""} ${QuebecVaccinationRegistryHelper.translateValueSetString(item.Model.DoseUnit, ValueSetType.DosageUnit)}`}</div>
                        };

                    case this.fieldNames.Status:
                        return {
                            renderCell: item => (
                                <div>
                                    {QuebecVaccinationRegistryHelper.translateValueSetString(
                                        item.Model.OverrideStatus,
                                        "ImmunizationHistoryStatus"
                                    )}
                                </div>
                            )
                        };

                    case this.fieldNames.Action:
                        return {
                            renderCell: item => {
                                if (this.props.isReadOnly)
                                    return (
                                        <div>
                                            <a onClick={() => this.props.onViewClick(item.Model)}>
                                                {cacheStore.GetString("ViewLabel")}
                                            </a>
                                        </div>
                                    );

                                return (
                                    <div>
                                        <a onClick={() => this.props.onViewClick(item.Model)}>
                                            {cacheStore.GetString("ViewLabel")}
                                        </a>
                                        {this.props.isCorrector && (
                                            <span>
                                                {" / "}
                                                <a onClick={() => this.props.onChangeStatusClick(item.Model)}>
                                                    {this.getStatusLabel(item.Model.OverrideStatus)}
                                                </a>
                                            </span>
                                        )}
                                        {QuebecVaccinationRegistryHelper.canDeleteImmunization(item.Model, this.props.immunizationDeletePermissions, this.props.isCorrector) && (
                                            <span>
                                                {" / "}
                                                <a onClick={() => this.props.onDeleteClick(item.Model)}>
                                                    {cacheStore.GetString("DeleteLabel")}
                                                </a>
                                            </span>
                                        )}
                                    </div>
                                );
                            }
                        };
                }
            },
            false
        );
    }

    private getStatusLabel(status: CodeValue): string {
        if (!status)
            return "";

        switch (status.Code) {
            case ImmunizationStatus.ForecastValid:
            case ImmunizationStatus.ForecastInvalid:
                return cacheStore.GetString(this.labels.Override);

            case ImmunizationStatus.OverriddenToInvalid:
            case ImmunizationStatus.OverriddenToValid:
                return cacheStore.GetString(this.labels.Reset);

            default:
                return "";
        }
    }
}

export { ImmunizationHistoryTable };
